<template>
  <v-select
    :id="inputId"
    v-model="internalValue"
    :options="regions"
    label="name"
    :reduce="reduceRegion"
    :filter="filterRegionsByPrefix"
    :placeholder="effectivePlaceholder"
    :clearable="clearable"
    :disabled="disabled || !countryId"
    class="region-select"
  />
</template>

<script>
import { mapGetters } from 'vuex'
import vSelect from 'vue-select'

export default {
  name: 'RegionSelect',
  components: {
    'v-select': vSelect,
  },
  props: {
    modelValue: {
      type: [String, Number],
      default: undefined,
    },
    value: {
      type: [String, Number],
      default: undefined,
    },
    countryId: {
      type: [String, Number],
      default: null,
    },
    inputId: {
      type: String,
      default: 'region-select',
    },
    placeholder: {
      type: String,
      default: 'Start typing region name',
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'input'],
  computed: {
    ...mapGetters({
      regionsByCountry: 'companies/regionsByCountry',
      regionsById: 'companies/regionsById',
    }),
    selectedRegionId() {
      const raw = this.modelValue !== undefined ? this.modelValue : this.value
      if (raw == null || raw === '') {
        return null
      }
      const id = Number(raw)
      return Number.isFinite(id) ? id : null
    },
    countryRegions() {
      if (!this.countryId) {
        return []
      }
      return this.regionsByCountry[String(this.countryId)] || []
    },
    regions() {
      const list = [...this.countryRegions]
      const selectedId = this.selectedRegionId
      if (selectedId == null) {
        return list
      }

      const alreadyListed = list.some((region) => Number(region.id) === selectedId)
      if (alreadyListed) {
        return list
      }

      const fallback = this.regionsById[String(selectedId)]
      if (fallback) {
        return [fallback, ...list]
      }

      return list
    },
    effectivePlaceholder() {
      if (!this.countryId) {
        return 'Select a country first'
      }
      return this.placeholder
    },
    internalValue: {
      get() {
        return this.selectedRegionId
      },
      set(val) {
        const next = val == null || val === '' ? null : Number(val)
        this.$emit('update:modelValue', Number.isFinite(next) ? next : null)
        this.$emit('input', Number.isFinite(next) ? next : null)
      },
    },
  },
  watch: {
    countryId: {
      immediate: true,
      handler(countryId, previousCountryId) {
        if (!countryId) {
          if (this.selectedRegionId != null) {
            this.internalValue = null
          }
          return
        }

        this.$store.dispatch('companies/loadRegions', countryId).then(() => {
          this.ensureSelectedRegionLoaded()

          if (
            previousCountryId != null
            && String(previousCountryId) !== String(countryId)
            && this.selectedRegionId != null
          ) {
            const stillValid = this.countryRegions.some(
              (region) => Number(region.id) === this.selectedRegionId,
            )
            if (!stillValid) {
              this.internalValue = null
            }
          }
        })
      },
    },
    selectedRegionId: {
      immediate: true,
      handler() {
        this.ensureSelectedRegionLoaded()
      },
    },
  },
  methods: {
    reduceRegion(region) {
      return region?.id != null ? Number(region.id) : null
    },
    ensureSelectedRegionLoaded() {
      const selectedId = this.selectedRegionId
      if (selectedId == null) {
        return
      }

      const listed = this.countryRegions.some((region) => Number(region.id) === selectedId)
      if (listed || this.regionsById[String(selectedId)]) {
        return
      }

      this.$store.dispatch('companies/loadRegion', selectedId)
    },
    filterRegionsByPrefix(options, search) {
      const term = String(search || '').trim().toLowerCase()
      if (!term) {
        return options
      }

      return options.filter((region) => (
        String(region.name || '').toLowerCase().startsWith(term)
        || String(region.short_name || '').toLowerCase().startsWith(term)
      ))
    },
  },
}
</script>
